"use client";

import dynamic from "next/dynamic";
import { BurnMultiplier } from "@/components/incinerator";

const FloatingFireParticles = dynamic(
  () => import("@/components/incinerator").then((m) => m.FloatingFireParticles),
  { ssr: false }
);

import { AlertTriangle, Loader2 } from "lucide-react";
import { AnimatePresence, m } from "motion/react";
import Image from "next/image";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { formatEther, formatUnits, parseEther } from "viem";
import { useAccount, useChainId, useSwitchChain } from "wagmi";
import { StatsCard } from "@/components/stats-card";
import { TVOverlay } from "@/components/tv-overlay";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PRIMARY_CHAIN_ID } from "@/constants/chain";
import { useLogin } from "@/hooks/auth";
import { usePrice } from "@/hooks/haiku";
import {
  useBurnAnimations,
  useBurnsInfinite,
  useTotalBurned,
} from "@/hooks/incinerator";
import { useBlockRefetch } from "@/hooks/use-block-refetch";
import { useTransactionToast } from "@/hooks/use-transaction-toast";
import {
  useReadHenloBalanceOf,
  useSimulateHenloTransfer,
  useWriteHenloTransfer,
} from "@/lib/onchain/generated";
import { cn, formatTokenIntelligent } from "@/utils";
import TokenAmountInput, { type TokenOption } from "@/components/token-amount-input";
import {
  ARCADE_STYLES,
  HIBERNATION_POKU_FILTER_LIVING,
  KANSEI_DEAD,
  TV_FRAME_BACKGROUND,
  TV_SCREEN_BACKGROUND,
} from "@/constants/arcade-styles";

// Dead address for burning tokens (MetaMask blocks transfers to zero address)
const BURN_ADDRESS = "0x000000000000000000000000000000000000dEaD" as const;

const BURN_SOURCE = {
  INCINERATOR: "incinerator",
  USER: "user",
  OVERUNDER: "overunder",
  BERATRACKR: "beratrackr",
} as const;

type BurnSourceKey = (typeof BURN_SOURCE)[keyof typeof BURN_SOURCE];

type HeatPalette = {
  bg: string;
  border: string;
  glow: string;
  text: string;
  timeText: string;
};

interface BurnSourceRowStyle {
  baseBackground?: string;
  hoverBackground?: string;
  borderRadius?: number;
  borderRight?: string;
  glowShadow?: string;
  frameBorder?: string;
  frameGradient?: string;
  flameSprite?: string;
  flameCount?: number;
  flameSizeClass?: string;
  flameGapClass?: string;
}

interface BurnSourceStyle {
  heatOverrides?: Partial<HeatPalette>;
  row?: BurnSourceRowStyle;
}

// AFTER HOURS: chrome-side row styling for the headline incinerator
// burn source. Originally a flame-orange highlight; here it reads as a
// recessed cobalt-rim screen so the activity feed stays in register.
const BURN_SOURCE_STYLES: Record<string, BurnSourceStyle> = {
  [BURN_SOURCE.INCINERATOR]: {
    heatOverrides: {
      bg: "rgba(62, 97, 250, 0.06)",
      border: "rgba(62, 97, 250, 0.35)",
      glow: "rgba(94, 123, 250, 0.4)",
      text: "rgba(155, 176, 240, 0.9)",
      timeText: "rgba(155, 176, 240, 0.45)",
    },
    row: {
      baseBackground: "linear-gradient(90deg, rgba(20,24,40,0.55), rgba(8,8,10,0.75))",
      hoverBackground: "linear-gradient(90deg, rgba(28,34,56,0.65), rgba(12,12,16,0.9))",
      borderRadius: 8,
      borderRight: "1px solid rgba(62, 97, 250, 0.22)",
      glowShadow: "0 0 2px rgba(94, 123, 250, 0.4)",
      frameBorder: "1px solid rgba(62, 97, 250, 0.22)",
      frameGradient: "linear-gradient(90deg, rgba(62,97,250,0.06), transparent)",
      flameSprite: "/incinerator/bera_fire.png",
      flameCount: 3,
      flameSizeClass: "h-6 w-6",
      flameGapClass: "gap-1",
    },
  },
} as const;

const INCINERATOR_TOKEN_OPTIONS: TokenOption[] = [
  { value: "henlo", label: "HENLO", icon: "/token/henlo.webp", iconAlt: "HENLO" },
];

const INCINERATOR_CTA_HOVER_SHADOW =
  "inset 0 2px 10px rgba(0, 0, 0, 0.8), inset 0 -1px 0 rgba(62, 97, 250, 0.35), 0 1px 0 rgba(255, 255, 255, 0.04), 0 0 12px rgba(62, 97, 250, 0.18)";
const INCINERATOR_BURN_CTA_HOVER_SHADOW =
  "inset 0 2px 10px rgba(0, 0, 0, 0.8), inset 0 -1px 0 rgba(62, 97, 250, 0.4), 0 1px 0 rgba(255, 255, 255, 0.04), 0 0 12px rgba(62, 97, 250, 0.22)";

const Page = () => {
  const { ref, inView } = useInView();
  // Separate observer to gate expensive visual effects
  const { ref: fxRef, inView: isFxInView } = useInView({
    rootMargin: "0px 0px -20% 0px",
  });
  const limit = 20;
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { login } = useLogin();
  const [burnAmount, setBurnAmount] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [hasSufficientBalance, setHasSufficientBalance] = useState(true);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [lastKnownBurnId, setLastKnownBurnId] = useState<string | null>(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Use the custom burn animations hook
  const {
    beraEyesFire,
    burnIntensity,
    screenShake,
    userBurnCount,
    burnStreak,
    showMultiplier,
    triggerBurnAnimation,
    resetAnimations,
    MULTIPLIER_WINDOW,
    MAX_MULTIPLIER,
  } = useBurnAnimations({ address });

  const { data: henloPrice } = usePrice({ symbol: "HENLO" });

  // Check if user is on the correct chain (Berachain)
  const isOnCorrectChain = chainId === PRIMARY_CHAIN_ID;

  // Read HENLO balance
  const { data: henloBalance, refetch: refetchHenloBalance } =
    useReadHenloBalanceOf({
      args: [address ?? "0x0"],
      query: {
        enabled: !!address,
      },
      chainId: PRIMARY_CHAIN_ID,
    });

  // Keep HENLO wallet balance fresh every 2 blocks while on this page
  useBlockRefetch({
    chainId,
    every: 2,
    enabled: !!address,
    refetch: [refetchHenloBalance],
  });

  // Simulate burn transaction
  const { data: simulateBurnData, error: simulateError } =
    useSimulateHenloTransfer({
      args: [BURN_ADDRESS, parseEther(burnAmount || "0")],
      query: {
        enabled:
          !!burnAmount &&
          parseFloat(burnAmount) > 0 &&
          !!address &&
          hasSufficientBalance,
      },
      chainId: PRIMARY_CHAIN_ID,
    });

  // Write contract for burning tokens
  const { data: burnTxHash, writeContractAsync: writeBurn } =
    useWriteHenloTransfer();

  // Use TanStack Query for burns data
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useBurnsInfinite(limit);

  // Flatten the pages to get all burns (already filtered in the hook)
  const burns = useMemo(() => data?.pages.flatMap((page) => page.burns) ?? [], [data?.pages]);
  const isLoadingMore = isFetchingNextPage;

  // Load more burns when scrolling
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Track new burns and trigger animations
  const [newBurnIds, setNewBurnIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Skip if no burns or still loading initial data
    if (!burns || burns.length === 0) {
      return;
    }

    const mostRecentBurn = burns[0];

    // On first load, just set the last known burn ID without animation
    if (isFirstLoad) {
      setLastKnownBurnId(mostRecentBurn.id);
      setIsFirstLoad(false);
      return;
    }

    // Check if we have a new burn (different ID than last known)
    if (mostRecentBurn.id !== lastKnownBurnId) {
      // New burn detected! Trigger animation
      triggerBurnAnimation(mostRecentBurn);
      setLastKnownBurnId(mostRecentBurn.id);

      // Track this as a new burn for entrance animation
      setNewBurnIds((prev) => new Set(prev).add(mostRecentBurn.id));

      // Remove from new burns after entrance animation completes
      setTimeout(() => {
        setNewBurnIds((prev) => {
          const updated = new Set(prev);
          updated.delete(mostRecentBurn.id);
          return updated;
        });
      }, 800); // Match entrance animation duration
    }
  }, [burns, lastKnownBurnId, isFirstLoad, triggerBurnAnimation]);

  // Check if user has sufficient balance for burn
  useEffect(() => {
    if (burnAmount && henloBalance !== undefined) {
      try {
        const parsedAmount = parseEther(burnAmount);
        setHasSufficientBalance(henloBalance >= parsedAmount);
      } catch (_err) {
        setHasSufficientBalance(false);
      }
    } else {
      setHasSufficientBalance(true); // Reset when amount is empty
    }
  }, [burnAmount, henloBalance]);

  // Transaction toast for burn
  const burnToast = useTransactionToast({
    title: "Burn HENLO tokens",
    description: burnAmount
      ? `${formatTokenIntelligent(parseEther(burnAmount || "0"), 18, true)} HENLO`
      : undefined,
    successImage: "/poku/toast/burn.png",
    onSuccess: async () => {
      // Reset amount and refresh balance when the tx confirms
      setBurnAmount("");
      await refetchHenloBalance();
    },
  });

  // Handle successful burn transaction
  const _handleBurnSuccess = async () => {
    // Kept for potential local side-effects; toast hook calls its own onSuccess
    setBurnAmount("");
    await refetchHenloBalance();
  };

  const isBurning = false;

  // Manual burn trigger
  const handleBurnClick = useCallback(async () => {
    if (!burnAmount || burnAmount === "0" || !address) {
      // Button already shows this state - no toast needed
      return;
    }

    if (!hasSufficientBalance) {
      // Button already shows "Insufficient Balance" - no toast needed
      return;
    }

    if (!isOnCorrectChain) {
      // User needs to switch network first
      return;
    }

    if (!simulateBurnData?.request) {
      // Simulation failed - likely insufficient balance or other issue
      return;
    }

    // Initiate transaction toast
    burnToast.initiate();

    try {
      // Execute the burn transaction
      const tx = await writeBurn(simulateBurnData.request);
      if (tx) {
        burnToast.trackTransaction(tx);
      }
    } catch (error: any) {
      burnToast.onException(error);
    }
  }, [
    burnAmount,
    address,
    hasSufficientBalance,
    isOnCorrectChain,
    simulateBurnData,
    writeBurn,
    burnToast,
  ]);

  // Use TanStack Query for total burned data
  const { data: totalBurnedData, isLoading: isLoadingTotalBurned } =
    useTotalBurned();

  const totalBurnedFormatted = Number(
    formatEther(BigInt(totalBurnedData?.totalBurned?.toString() || "0"))
  );
  const uniqueBurners = Number(totalBurnedData?.uniqueBurners ?? 0);

  // Format numbers for display
  const formatCompactNumber = (num: number): string => {
    if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return num.toFixed(0);
  };

  // Handle input change for amount
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/,/g, "");
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setBurnAmount(value);
    }
  };

  // Handle quick-select ratios for burn amount
  const handleQuickSelect = useCallback(
    (ratio: number) => {
      if (henloBalance === undefined) return;
      const scaledRatio = BigInt(Math.round(ratio * 10_000));
      const amount = (henloBalance * scaledRatio) / 10_000n;
      setBurnAmount(formatUnits(amount, 18));
    },
    [henloBalance],
  );

  const displayBurnAmount = useMemo(() => {
    if (!isFocused && burnAmount) {
      const numeric = Number(burnAmount);
      if (!Number.isNaN(numeric)) {
        return numeric.toLocaleString("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
          useGrouping: true,
        });
      }
    }
    return burnAmount;
  }, [burnAmount, isFocused]);


  // Handle switch network
  const handleSwitchNetwork = () => {
    if (switchChain) {
      switchChain({ chainId: PRIMARY_CHAIN_ID });
    }
  };

  // Calculate burn rate (mock data for now)
  // const burnRate = "2.5K"; // TODO: Calculate from recent burns when needed
  // Supply percentage burned — denom is 100B HENLO total supply (100,000,000,000)
  const HENLO_TOTAL_SUPPLY = 100_000_000_000;
  const burnPercentage = (
    (totalBurnedFormatted / HENLO_TOTAL_SUPPLY) *
    100
  ).toFixed(1);
  const totalBurnedUSD = (henloPrice?.usd || 0) * totalBurnedFormatted;

  // Dev toolbar visibility state
  const [showDevTools, setShowDevTools] = useState(false);

  // Toggle dev tools with keyboard shortcut (Ctrl/Cmd + D)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "d") {
        e.preventDefault();
        setShowDevTools((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  return (
    <m.div
      className="mx-auto min-h-screen w-full max-w-6xl overflow-y-auto scrollbar-hide"
      animate={
        screenShake && isFxInView
          ? { x: [0, -2, 2, -2, 2, 0], y: [0, -1, 1, -1, 1, 0] }
          : {}
      }
      transition={{
        duration: 0.5,
        ease: "easeOut",
      }}
    >
      <div
        ref={fxRef}
        className="relative mx-auto mt-8 md:mt-16 p-4 font-inter scrollbar-hide"
      >
        {/* Dev Toolbar - Fixed to bottom right */}
        {showDevTools && (
          <div className="border-orange-500/50 fixed bottom-4 right-4 z-50 rounded-lg border bg-black/90 p-4 backdrop-blur-sm">
            <div className="text-orange-400 mb-2 font-pressStart2P text-xs">
              DEV TOOLS
            </div>
            <div className="space-y-2">
              <Button
                size="sm"
                variant="outline"
                className="border-orange-500/30 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 w-full"
                onClick={() => {
                  triggerBurnAnimation({
                    id: `test-${Date.now()}-${Math.random()}`,
                    amount: BigInt(Math.random() * 10000000 * 1e18).toString(),
                    from: address || "0x0",
                    timestamp: Date.now().toString(),
                    source: ["incinerator", "user", "overunder", "beratrackr"][
                      Math.floor(Math.random() * 4)
                    ] as any,
                  });
                }}
              >
                🔥 Trigger Burn
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 w-full"
                onClick={() => {
                  triggerBurnAnimation({
                    id: `test-large-${Date.now()}`,
                    amount: BigInt(2000000 * 1e18).toString(),
                    from: address || "0x0",
                    timestamp: Date.now().toString(),
                    source: "incinerator" as const,
                  });
                }}
              >
                💥 Large Burn (Shake)
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 w-full"
                onClick={() => {
                  resetAnimations();
                }}
              >
                🔄 Reset All
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-500/30 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 w-full"
                onClick={() => {
                  // Simulate rapid burns for multiplier
                  const rapidBurn = () => {
                    triggerBurnAnimation({
                      id: `rapid-${Date.now()}-${Math.random()}`,
                      amount: BigInt(100000 * 1e18).toString(),
                      from: address || "0x0",
                      timestamp: Date.now().toString(),
                      source: "incinerator" as const,
                    });
                  };

                  rapidBurn(); // First burn
                  setTimeout(rapidBurn, 500); // 2x
                  setTimeout(rapidBurn, 1000); // 3x
                  setTimeout(rapidBurn, 1500); // 4x
                  setTimeout(rapidBurn, 2000); // 5x
                }}
              >
                ⚡ Test 5x Combo
              </Button>
              <div className="border-orange-500/30 mt-2 border-t pt-2">
                <div className="text-xs text-white/60">
                  <div>Intensity: {burnIntensity.toFixed(1)}/10</div>
                  <div>User Burns: {userBurnCount}</div>
                  <div>Eye Fire: {beraEyesFire ? "ON" : "OFF"}</div>
                  <div>
                    Multiplier: {burnStreak > 0 ? `${burnStreak}x` : "None"}
                  </div>
                  <div>Combo Active: {showMultiplier ? "YES" : "NO"}</div>
                </div>
              </div>
            </div>
            <div className="mt-2 text-[10px] text-white/40">
              Press Cmd+D to toggle
            </div>
          </div>
        )}

        {/* AFTER HOURS: viewport-wide orange/red burn glow + scattered
            bright ember sprites removed. Bright fire bleed across the
            chrome violates the dead-room atmosphere; the burn theme
            stays inside the canvas (Bera character + canvas particles). */}
        {/* Main Content Area - Two Columns */}
        <div className="flex flex-col gap-4 lg:flex-row">
          {/* Left Column content becomes separable on mobile */}
          <div className="contents lg:flex lg:flex-col lg:flex-[0.5]">
            {/* Stats Cards Grid — AFTER HOURS cobalt-rim cabinet (was
                fire-red frame). Borrows TV_FRAME chrome vocabulary so
                the panel sits in register with the lobby cabinets. */}
            <div
              className="order-1 lg:order-none relative mb-4 w-full rounded-md p-4"
              style={{
                ...ARCADE_STYLES.TV_FRAME,
                backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_FRAME_BACKGROUND}`,
              }}
            >
{/* Title in top left — barely-alive filter, no orange
                  drop-shadow. Filter borrowed from the navbar fan-out
                  characters so the room logo sits in the same register. */}
              <div className="absolute -top-8 md:left-8 md:-top-8 z-10 flex items-center gap-2">
                <div className="relative h-16 w-48">
                  <Image
                    src="/lobby/incinerator.svg"
                    alt="HENLO INCINERATOR"
                    fill
                    sizes="192px"
                    className="kansei-flicker-living-b object-contain"
                    style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
                  />
                </div>
              </div>

              {/* Educational TV Banner — moondust copy + dying flicker.
                  No bright white text on chrome. */}
              <div className="relative mb-2 mt-5">
                <div className="relative overflow-hidden rounded-md">
                  <div className="relative z-10 px-2 py-1.5">
                    <div className="flex flex-col gap-0 leading-tight">
                      <p className="kansei-flicker-b font-pressStart2P text-[9px] uppercase tracking-wider text-zinc-500">
                        PERMANENTLY BURN HENLO TOKENS TO REDUCE
                      </p>
                      <p className="kansei-flicker-c font-pressStart2P text-[9px] uppercase tracking-wider text-zinc-500">
                        THE CIRCULATING SUPPLY.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* StatsCard — single moondust ghost. All four tiles share
                  the cobalt-tinted moondust palette so we drop the
                  per-tile chromatic CRT signal (was orange / red /
                  amber / pink dots and text). */}
              <div className="relative z-10 grid grid-cols-2 gap-2">
                <StatsCard
                  value={formatCompactNumber(totalBurnedFormatted)}
                  label="Total Burned"
                  dotColor="#5e7bfa"
                  textColor="rgba(155, 176, 240, 0.9)"
                  bgColor="#0e0f15"
                  isLoading={isLoadingTotalBurned}
                />
                <StatsCard
                  value={formatCompactNumber(uniqueBurners)}
                  label="Arsonists"
                  dotColor="#5e7bfa"
                  textColor="rgba(155, 176, 240, 0.9)"
                  bgColor="#0e0f15"
                  isLoading={isLoadingTotalBurned}
                />
                <StatsCard
                  value={`${burnPercentage}%`}
                  label="Supply Burned"
                  dotColor="#5e7bfa"
                  textColor="rgba(155, 176, 240, 0.9)"
                  bgColor="#0e0f15"
                  isLoading={false}
                />
                <StatsCard
                  value={`$${formatCompactNumber(totalBurnedUSD)}`}
                  label="USD Value"
                  dotColor="#5e7bfa"
                  textColor="rgba(155, 176, 240, 0.9)"
                  bgColor="#0e0f15"
                  isLoading={isLoadingTotalBurned}
                />
              </div>
            </div>

            {/* Activity Section — AFTER HOURS cobalt-rim cabinet.
                Thermometer chromatic gradient + breathing heat waves
                stripped; replaced with TV_SCREEN cobalt-tinted recess
                + scanline atmosphere so the burn feed reads as cooled
                CRT telemetry. */}
            <div
              className="order-3 lg:order-none w-full rounded-md p-4"
              style={{
                ...ARCADE_STYLES.TV_FRAME,
                backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_FRAME_BACKGROUND}`,
                contentVisibility: "auto",
                containIntrinsicSize: "432px",
              }}
            >
              <div
                className="relative flex h-[400px] flex-col overflow-hidden rounded-md"
                style={{
                  ...ARCADE_STYLES.TV_SCREEN,
                  backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_SCREEN_BACKGROUND}`,
                  minHeight: undefined,
                }}
              >
                <TVOverlay intensity="light" />

                {/* Burn Stack - Newest at Bottom (builds up) */}
                <div className="relative z-10 flex h-full flex-col">
                  {/* Activity content that scrolls */}
                  <div className="absolute inset-0 overflow-hidden">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2
                          className="size-6 animate-spin"
                          style={{ color: "rgba(155, 176, 240, 0.85)" }}
                        />
                      </div>
                    ) : error ? (
                      <div
                        className="rounded p-3 text-center"
                        style={{ background: "rgba(62, 97, 250, 0.06)" }}
                      >
                        <p
                          className="kansei-flicker-b font-pressStart2P text-[9px] uppercase"
                          style={{ color: "rgba(155, 176, 240, 0.85)" }}
                        >
                          ERROR LOADING BURN ACTIVITY
                        </p>
                      </div>
                    ) : burns.length > 0 ? (
                      <ScrollArea className="h-full p-4" ref={scrollAreaRef}>
                        <div className="flex flex-col gap-0.5 pb-20">
                          <AnimatePresence initial={false}>
                            {burns.map((burn: any, index) => (
                              <BurnLogItem
                                key={burn.id}
                                burn={burn}
                                index={burns.length - 1 - index}
                                isNewest={index === 0}
                                isNewBurn={newBurnIds.has(burn.id)}
                              />
                            ))}
                          </AnimatePresence>
                          {/* Load more when scrolling */}
                          {hasNextPage && (
                            <div ref={ref} className="py-4 text-center">
                              {isLoadingMore && (
                                <Loader2
                                  className="mx-auto size-4 animate-spin"
                                  style={{ color: "rgba(155, 176, 240, 0.85)" }}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    ) : (
                      <div
                        className="mb-4 rounded p-3 text-center"
                        style={{ background: "rgba(62, 97, 250, 0.06)" }}
                      >
                        <div className="flex flex-col gap-0">
                          <p
                            className="kansei-flicker-b font-pressStart2P text-[9px] uppercase"
                            style={{ color: "rgba(155, 176, 240, 0.85)" }}
                          >
                            NO BURNS YET
                          </p>
                          <p
                            className="kansei-flicker-c mt-1 font-pressStart2P text-[8px] uppercase"
                            style={{ color: "rgba(155, 176, 240, 0.55)" }}
                          >
                            BE THE FIRST
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Enhanced Floating Fire Particles Above Activity */}
                  {isFxInView && (
                    <div className="pointer-events-none absolute inset-0 z-20">
                      <FloatingFireParticles
                        burnIntensity={burnIntensity}
                        burnStreak={burnStreak}
                      />
                    </div>
                  )}

                  {/* Bottom Header - Positioned Over Fade Area */}
                  <div
                    className="pointer-events-none absolute bottom-0 left-0 right-0 z-30 flex items-end justify-center pb-2"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(20, 20, 22, 0.98) 0%, rgba(20, 20, 22, 0.9) 20%, rgba(20, 20, 22, 0.7) 40%, rgba(20, 20, 22, 0.3) 70%, transparent 100%)",
                      height: "80px",
                    }}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <div className="relative h-4 w-28">
                        <Image
                          src="/lobby/incinerator.svg"
                          alt="INCINERATOR"
                          fill
                          sizes="112px"
                          className="kansei-flicker-living-c object-contain"
                          style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
                        />
                      </div>
                      <p
                        className="kansei-flicker-b font-pressStart2P text-[7px] uppercase tracking-[0.2em]"
                        style={{ color: "rgba(155, 176, 240, 0.7)" }}
                      >
                        Live burns
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column — AFTER HOURS cobalt-rim cabinet (was
              fire-red frame). Same TV_FRAME chrome as the stats and
              activity cabinets. */}
          <div
            className="order-2 lg:order-none relative h-fit w-full rounded-md p-4 lg:max-w-[540px] lg:flex-[0.5]"
            style={{
              ...ARCADE_STYLES.TV_FRAME,
              backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_FRAME_BACKGROUND}`,
            }}
          >
            <div className="relative z-10 flex flex-col gap-4">

              {/* Burn Panel — AFTER HOURS cobalt-rim screen (was
                  fire-orange border + glow). Inherits TV_SCREEN recess
                  + scanline so the form sits inside a cooled CRT, not
                  a lit fire panel. */}
              <div
                className="relative flex grow flex-col overflow-hidden rounded-md p-1"
                style={{
                  ...ARCADE_STYLES.TV_SCREEN,
                  backgroundImage: `${KANSEI_DEAD.SCANLINE_BG_2}, ${TV_SCREEN_BACKGROUND}`,
                  minHeight: undefined,
                }}
              >
                {/* TV Overlay Effect */}
                <TVOverlay intensity="light" />

                {/* Bera Burn Character — barely-alive filter in place
                    of the orange drop-shadow halo. The character is the
                    operator-facing personality; LIVING register reads
                    "still working, still here, just dimmed." */}
                <div className="pointer-events-none absolute -bottom-8 -left-4 z-20 size-48">
                  <div className="relative size-full">
                    <Image
                      src="/incinerator/bera_burn.png"
                      alt="Bera with flamethrower"
                      fill
                      sizes="192px"
                      className="kansei-flicker-living-a object-contain"
                      style={{ filter: HIBERNATION_POKU_FILTER_LIVING }}
                    />

                    {/* Multiplier Display */}
                    <BurnMultiplier
                      burnStreak={burnStreak}
                      showMultiplier={showMultiplier}
                    />

                    {/* Fire in Bera's Eyes */}
                    <AnimatePresence>
                      {beraEyesFire && (
                        <>
                          {/* Left Eye Fire */}
                          <m.div
                            className="absolute left-[28px] top-[75px] z-30 h-12 w-8"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{
                              scale: [0, 1.2, 1, 1.1, 1],
                              opacity: [0, 1, 0.9, 1, 0.8],
                            }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{
                              duration: 2.5,
                              ease: "easeOut",
                              times: [0, 0.1, 0.5, 0.8, 1],
                            }}
                          >
                            <Image
                              src="/incinerator/fire_eyes.png"
                              alt=""
                              fill
                              sizes="32px"
                              className="object-contain brightness-125"
                            />
                          </m.div>

                          {/* Right Eye Fire */}
                          <m.div
                            className="absolute left-[83px] top-[69px] z-30 h-12 w-8"
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{
                              scale: [0, 1.2, 1, 1.1, 1],
                              opacity: [0, 1, 0.9, 1, 0.8],
                            }}
                            exit={{ scale: 0, opacity: 0 }}
                            transition={{
                              duration: 2.5,
                              ease: "easeOut",
                              delay: 0.1,
                              times: [0, 0.1, 0.5, 0.8, 1],
                            }}
                          >
                            <Image
                              src="/incinerator/fire_eyes.png"
                              alt=""
                              fill
                              sizes="32px"
                              className="object-contain brightness-125"
                            />
                          </m.div>

                          {/* Eye Glow Effect */}
                          <m.div
                            className="absolute left-14 top-14 h-8 w-20"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [0, 0.6, 0.4, 0.7, 0] }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 2.5, ease: "easeOut" }}
                          >
                            <div
                              className="size-full rounded-full"
                              style={{
                                background:
                                  "radial-gradient(circle, rgba(255, 255, 0, 0.4) 0%, rgba(255, 107, 53, 0.3) 40%, transparent 70%)",
                                filter: "blur(6px)",
                              }}
                            />
                          </m.div>
                        </>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Decorative cobalt residue (was orange Y2K wedge). */}
                <div
                  className="pointer-events-none absolute -left-6 bottom-0 size-32"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(62, 97, 250, 0.08) 0%, transparent 70%)",
                    clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                    transform: "rotate(45deg)",
                  }}
                />

                {/* Header with Burn Title — moondust + dying flicker.
                    No bright white text on chrome. */}
                <div className="relative z-10 p-3 pb-2">
                  <div className="mb-8">
                    <h3
                      className="kansei-flicker-b font-pressStart2P text-xs uppercase tracking-[0.18em] text-zinc-500"
                      style={{ textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)" }}
                    >
                      Burn Tokens
                    </h3>
                  </div>

                  {/* Form Content */}
                  <div className="flex grow flex-col space-y-4">
                    {/* Amount Input Section */}
                    <div className="mt-auto">
                      <TokenAmountInput
                        label="Amount"
                        amount={isFocused ? burnAmount : displayBurnAmount}
                        onAmountChange={handleAmountChange}
                        onAmountFocus={() => setIsFocused(true)}
                        onAmountBlur={() => setIsFocused(false)}
                        token={INCINERATOR_TOKEN_OPTIONS[0].value}
                        tokenOptions={INCINERATOR_TOKEN_OPTIONS}
                        readOnlyToken
                        disabled={isBurning}
                        placeholder={isFocused ? "" : "0"}
                        onQuickSelect={handleQuickSelect}
                        quickActionsDisabled={henloBalance === undefined || isBurning}
                        balance={address ? henloBalance : null}
                      />
                    </div>

                    {/* Warning Message — cobalt-rim recess + moondust
                        copy. The warning is critical operator info but
                        sits inside the dead room, so it reads as a
                        cooled-CRT advisory, not a flame-orange alert. */}
                    <div
                      className="rounded-lg p-3"
                      style={{
                        background: "rgba(0, 0, 0, 0.25)",
                        border: "1px solid rgba(62, 97, 250, 0.18)",
                      }}
                    >
                      <div className="flex items-start gap-2.5">
                        <AlertTriangle
                          className="mt-1 size-4"
                          style={{ color: "rgba(155, 176, 240, 0.6)" }}
                        />
                        <div className="flex flex-col gap-1">
                          <p
                            className="kansei-flicker-b font-pressStart2P text-[9px] uppercase tracking-wider"
                            style={{ color: "rgba(155, 176, 240, 0.85)" }}
                          >
                            BURNING IS PERMANENT
                          </p>
                          <div className="flex flex-col gap-0 leading-tight">
                            <p
                              className="kansei-flicker-c font-pressStart2P text-[8px] uppercase"
                              style={{ color: "rgba(155, 176, 240, 0.55)" }}
                            >
                              TOKENS SENT TO THE BURN ADDRESS CAN NEVER BE
                              RECOVERED
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Burn Button — AFTER HOURS recessed cobalt-rim
                        screen (was bright orange/yellow gradient with
                        breathing fire glow). Primary CTA keeps a
                        slightly brighter label alpha (~1.0) than the
                        passive sub-nav Login (~0.85) so the action
                        affordance is preserved without bringing back
                        the chromatic halo. */}
                    <div className="relative mt-4 pb-32">
                      {!address ? (
                        <button
                          type="button"
                          onClick={() => login()}
                          className="incinerator-cta-kansei group relative flex h-16 w-full items-center justify-center overflow-hidden rounded-md font-pressStart2P text-[10px] uppercase tracking-[0.22em]"
                          style={{
                            ...ARCADE_STYLES.TICKER_CONTAINER,
                            "--incinerator-cta-hover-shadow": INCINERATOR_CTA_HOVER_SHADOW,
                            transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}`,
                          } as React.CSSProperties}
                        >
                          <TVOverlay intensity="light" />
                          <span
                            className="kansei-flicker-living-c relative z-10"
                            style={{
                              color: "rgba(155, 176, 240, 0.85)",
                              textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                            }}
                          >
                            Sign in to Incinerate
                          </span>
                        </button>
                      ) : !isOnCorrectChain ? (
                        <button
                          type="button"
                          onClick={handleSwitchNetwork}
                          className="incinerator-cta-kansei group relative flex h-16 w-full items-center justify-center overflow-hidden rounded-md font-pressStart2P text-[10px] uppercase tracking-[0.22em]"
                          style={{
                            ...ARCADE_STYLES.TICKER_CONTAINER,
                            "--incinerator-cta-hover-shadow": INCINERATOR_CTA_HOVER_SHADOW,
                            transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}`,
                          } as React.CSSProperties}
                        >
                          <TVOverlay intensity="light" />
                          <span
                            className="kansei-flicker-living-c relative z-10"
                            style={{
                              color: "rgba(155, 176, 240, 0.85)",
                              textShadow: "0 1px 0 rgba(0, 0, 0, 0.7)",
                            }}
                          >
                            Switch to Berachain
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleBurnClick}
                          disabled={
                            !burnAmount ||
                            burnAmount === "0" ||
                            !hasSufficientBalance ||
                            isBurning
                          }
                          className="incinerator-cta-kansei group relative flex h-16 w-full items-center justify-center overflow-hidden rounded-md font-pressStart2P text-[10px] uppercase tracking-[0.22em] disabled:cursor-not-allowed disabled:opacity-50"
                          style={{
                            ...ARCADE_STYLES.TICKER_CONTAINER,
                            "--incinerator-cta-hover-shadow": INCINERATOR_BURN_CTA_HOVER_SHADOW,
                            transition: `${KANSEI_DEAD.TRANSITION.BORDER_QUANTUM}, ${KANSEI_DEAD.TRANSITION.SHADOW_QUANTUM}`,
                          } as React.CSSProperties}
                        >
                          <TVOverlay intensity="light" />
                          <span
                            className="kansei-flicker-living-b relative z-10"
                            style={{
                              color: "rgba(200, 215, 255, 1)",
                              textShadow:
                                "0 1px 0 rgba(0, 0, 0, 0.7), 0 0 1px rgba(94, 123, 250, 0.3)",
                            }}
                          >
                            {isBurning
                              ? "Burning..."
                              : !hasSufficientBalance && burnAmount
                                ? "Insufficient Balance"
                                : "Incinerate"}
                          </span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .incinerator-cta-kansei:not(:disabled):hover {
          box-shadow: var(--incinerator-cta-hover-shadow) !important;
        }
      `}</style>
    </m.div>
  );
};

export default Page;

// Fire level ranges - configurable constants
const FIRE_LEVELS = {
  SMALL: { min: 0, max: 1000, count: 1 }, // < 1K - 1 fire
  MEDIUM: { min: 1000, max: 100000, count: 2 }, // 1K - 100K - 2 fires
  LARGE: { min: 100000, max: 1000000, count: 3 }, // 100K - 1M - 3 fires
  MEGA: { min: 1000000, max: Infinity, count: 4 }, // > 1M - 4 fires
} as const;

// Helper function to get fire count based on burn amount
function getFireCount(amount: string): number {
  const numAmount = Number(formatEther(BigInt(amount)));

  if (numAmount < FIRE_LEVELS.SMALL.max) return FIRE_LEVELS.SMALL.count;
  if (numAmount < FIRE_LEVELS.MEDIUM.max) return FIRE_LEVELS.MEDIUM.count;
  if (numAmount < FIRE_LEVELS.LARGE.max) return FIRE_LEVELS.LARGE.count;
  return FIRE_LEVELS.MEGA.count;
}

// Burn Log Item Component with Thermometer Heat Effect and Entrance Animations
const BurnLogItem = memo(function BurnLogItem({
  burn,
  index,
  isNewest,
  isNewBurn = false,
}: {
  burn: {
    id?: string;
    amount: string;
    timestamp: string;
    txHash?: string | null;
    transactionHash?: string | null;
    from?: string;
    source?:
      | "incinerator"
      | "overunder"
      | "beratrackr"
      | "user"
      | "other"
      | string;
  };
  index: number;
  isNewest?: boolean;
  isNewBurn?: boolean;
}) {
  const [hover, setHover] = useState(false);

  // AFTER HOURS: heat-tier chromatic gradient (yellow → orange → red →
  // purple → blue) collapsed to a single moondust ghost. Index drives
  // alpha falloff only — the single-cobalt-ghost doctrine forbids the
  // per-card chromatic rainbow.
  const heatIntensity = Math.max(0, 1 - index * 0.05);
  const fireCount = getFireCount(burn.amount);

  const getHeatColors = () => {
    const textAlpha = Math.max(0.45, 0.92 - index * 0.04);
    const timeAlpha = Math.max(0.2, 0.6 - index * 0.04);
    const borderAlpha = Math.max(0.12, 0.4 - index * 0.025);
    const bgAlpha = Math.max(0.02, 0.08 - index * 0.005);
    const glowAlpha = Math.max(0.1, heatIntensity * 0.4);

    return {
      bg: `rgba(62, 97, 250, ${bgAlpha})`,
      border: `rgba(62, 97, 250, ${borderAlpha})`,
      glow: `rgba(94, 123, 250, ${glowAlpha})`,
      text: `rgba(155, 176, 240, ${textAlpha})`,
      timeText: `rgba(155, 176, 240, ${timeAlpha})`,
    };
  };

  const sourceStyle = BURN_SOURCE_STYLES[burn.source ?? ""] ?? {};
  const baseHeatColors = getHeatColors();
  const heatColors = sourceStyle.heatOverrides
    ? { ...baseHeatColors, ...sourceStyle.heatOverrides }
    : baseHeatColors;
  const rowStyle = sourceStyle.row;
  const rowBorderRadius = rowStyle?.borderRadius;
  const borderRadiusValue = rowBorderRadius !== undefined ? `${rowBorderRadius}px` : undefined;
  const rowBaseBackground = rowStyle?.baseBackground;
  const rowHoverBackground = rowStyle?.hoverBackground;
  const rowBorderRight = rowStyle?.borderRight;
  const rowGlowShadow = rowStyle?.glowShadow;
  const rowFrameBorder = rowStyle?.frameBorder;
  const rowFrameGradient = rowStyle?.frameGradient;
  const flameSprite = rowStyle?.flameSprite;
  const flameCount = rowStyle?.flameCount ?? 0;
  const flameSizeClass = rowStyle?.flameSizeClass ?? "h-5 w-5";
  const flameGapClass = rowStyle?.flameGapClass ?? "gap-1";
  const hasFlameStack = Boolean(flameSprite && flameCount > 0);

  function formatTimestamp(timestamp: string) {
    try {
      // Convert from seconds to milliseconds (Envio returns timestamps in seconds)
      const timestampMs = Number(timestamp) * 1000;
      const date = new Date(timestampMs);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();

      const units = [
        { name: "day", ms: 24 * 60 * 60 * 1000 },
        { name: "hour", ms: 60 * 60 * 1000 },
        { name: "minute", ms: 60 * 1000 },
        { name: "second", ms: 1000 },
      ];

      for (const unit of units) {
        const diff = Math.floor(diffMs / unit.ms);
        if (diff > 0) {
          return `${diff} ${unit.name}${diff > 1 ? "s" : ""} ago`;
        }
      }

      return "just now";
    } catch (_error) {
      return "unknown time";
    }
  }

  // Use intelligent formatting that shows more decimals for smaller amounts
  const amount = formatTokenIntelligent(BigInt(burn.amount), 18, true);
  const txHash = burn.txHash ?? burn.transactionHash ?? null;

  const Wrapper = txHash ? m.a : m.div;
  const props = txHash
    ? {
        href: `http://beratrail.io/tx/${txHash}`,
        target: "_blank",
        rel: "noopener noreferrer",
      }
    : {};

  // Calculate burn amount for size-based effects
  const burnAmount = Number(formatEther(BigInt(burn.amount)));

  return (
    <Wrapper
      {...props}
      layout="position"
      layoutId={txHash || burn.id || burn.amount + burn.timestamp}
      initial={
        isNewBurn
          ? {
              x: 300,
              opacity: 0,
              scale: 0.8,
              rotateY: -25,
            }
          : false
      }
      animate={{
        x: 0,
        opacity: 1,
        scale: 1,
        rotateY: 0,
      }}
      exit={{
        x: -100,
        opacity: 0,
        scale: 0.9,
        transition: {
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
        },
      }}
      transition={{
        layout: {
          type: "spring",
          bounce: 0.15,
          duration: 0.4,
        },
        x: {
          type: "spring",
          bounce: 0,
          duration: isNewBurn ? 0.6 : 0.3,
          delay: isNewBurn ? 0.35 : 0, // Wait for list to shift down first
        },
        opacity: {
          duration: isNewBurn ? 0.5 : 0.3,
          delay: isNewBurn ? 0.35 : 0,
        },
        scale: {
          type: "spring",
          bounce: 0,
          duration: isNewBurn ? 0.6 : 0.3,
          delay: isNewBurn ? 0.35 : 0,
        },
        rotateY: {
          type: "spring",
          bounce: 0,
          duration: isNewBurn ? 0.6 : 0.3,
          delay: isNewBurn ? 0.35 : 0,
        },
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn(
        "group relative flex items-center justify-between rounded-sm px-2 py-2 transition-[background,box-shadow] duration-200",
        txHash && "cursor-pointer"
      )}
      style={{
        background: hover
          ? rowHoverBackground ?? `linear-gradient(90deg, ${heatColors.bg}, rgba(0,0,0,0.15))`
          : rowBaseBackground ?? heatColors.bg,
        borderLeft: `3px solid ${heatColors.border}`,
        borderRadius: borderRadiusValue,
        borderRight: rowBorderRight,
        boxShadow: hover
          ? `inset 0 0 10px ${heatColors.glow}`
          : isNewest
            ? `inset 0 0 8px ${heatColors.glow}`
            : rowGlowShadow ?? "none",
      }}
    >
      {rowFrameBorder && (
        <div
          className="pointer-events-none absolute inset-[1px]"
          style={{ borderRadius: borderRadiusValue, border: rowFrameBorder }}
        />
      )}
      {rowFrameGradient && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            borderRadius: borderRadiusValue,
            background: rowFrameGradient,
          }}
        />
      )}
      {/* Side flame stack — no animate-pulse (sine-wave fade is a
          KANSEI anti-pattern). Static + dim with whisper cobalt halo. */}
      {hasFlameStack && flameSprite && (
        <div
          className={cn(
            "pointer-events-none absolute -left-6 top-1/2 hidden -translate-y-1/2 flex-col opacity-50 lg:flex",
            flameGapClass,
          )}
        >
          {Array.from({ length: flameCount }).map((_, i) => (
            <div key={i} className={cn("relative", flameSizeClass)}>
              <Image
                src={flameSprite}
                alt="Fire"
                fill
                sizes="24px"
                className="object-contain"
                style={{
                  filter:
                    "grayscale(0.7) brightness(0.72) drop-shadow(0 0 1px rgba(94, 123, 250, 0.3))",
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Heat Wave Entrance Effect - New Burns Only */}
      {isNewBurn && (
        <m.div
          className="pointer-events-none absolute inset-0 z-20"
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: "100%", opacity: [0, 0.6, 0] }}
          transition={{
            duration: 0.8,
            ease: [0.4, 0, 0.2, 1],
            delay: 0.45, // Sync with entrance animation
          }}
          style={{
            background: `linear-gradient(90deg, 
              transparent, 
              ${heatColors.glow}40, 
              ${heatColors.glow}80, 
              ${heatColors.glow}40, 
              transparent)`,
          }}
        />
      )}

      {/* Thermal Glow Pulse for New Large Burns */}
      {isNewBurn && burnAmount > 100000 && (
        <m.div
          className="pointer-events-none absolute inset-0 rounded-sm"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: [0, 0.3, 0],
            scale: [0.8, 1.1, 1],
          }}
          transition={{
            type: "tween",
            duration: 1.2,
            ease: [0.4, 0, 0.2, 1],
            delay: 0.5, // Sync with entrance animation
          }}
          style={{
            background: `radial-gradient(circle, ${heatColors.glow}30 0%, transparent 70%)`,
            filter: "blur(8px)",
          }}
        />
      )}

      {/* AFTER HOURS: ripple scale tweens, breathing heat-shimmer
          (fireGlow infinite + easeInOut), and heat-wave pulse all
          stripped — these are the canonical "alive CRT" signals.
          New-burn entrance + heat wave (above) carry the signal. */}
      {isNewest && (
        <m.div
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.18 }}
          transition={{ duration: 0.166, ease: "linear" }}
          style={{
            background: `linear-gradient(90deg, transparent, ${heatColors.glow}, transparent)`,
          }}
        />
      )}

      <div className="relative z-10 flex items-center gap-3">
        {/* Source Icon - First */}
        <TooltipProvider>
          <m.div
            className="w-10 flex-shrink-0"
            initial={isNewBurn ? { scale: 0, rotate: -180 } : false}
            animate={{ scale: 1, rotate: 0 }}
            transition={{
              type: "spring",
              bounce: 0,
              duration: 0.6,
              delay: isNewBurn ? 0.55 : 0, // After entrance animation starts
            }}
          >
            {burn.source === BURN_SOURCE.INCINERATOR ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative h-6 w-16">
                    <Image
                      src="/lobby/incinerator.svg"
                      alt="Incinerator"
                      fill
                      sizes="40px"
                      className="object-contain"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <div className="relative size-6 flex-shrink-0">
                      <Image
                        src="/lobby/incinerator.svg"
                        alt="Incinerator"
                        fill
                        sizes="24px"
                        className="object-contain"
                      />
                    </div>
                    <div className="flex flex-col">
                      <p className="font-semibold">Incinerator Burn</p>
                      {burn.from && (
                        <p className="text-xs text-muted-foreground">
                          {burn.from.slice(0, 6)}...{burn.from.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : burn.source === BURN_SOURCE.USER ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative h-4 w-16">
                    <Image
                      src="/lobby/incineraffle.svg"
                      alt="User burn"
                      fill
                      sizes="40px"
                      className="object-contain opacity-60"
                      style={{
                        filter:
                          "grayscale(0.7) brightness(0.72) drop-shadow(0 0 1px rgba(94, 123, 250, 0.3))",
                      }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <div className="relative size-6 flex-shrink-0">
                      <Image
                        src="/lobby/incineraffle.svg"
                        alt="Incineraffle"
                        fill
                        sizes="24px"
                        className="object-contain"
                      />
                    </div>
                    <div className="flex flex-col">
                      <p className="font-semibold">
                        Incineraffle Burn
                      </p>
                      {burn.from && (
                        <p className="text-xs text-muted-foreground">
                          {burn.from.slice(0, 6)}...{burn.from.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : burn.source === BURN_SOURCE.OVERUNDER ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex size-4 items-center justify-center rounded-full"
                    style={{ background: "rgba(62, 97, 250, 0.15)" }}
                  >
                    <span
                      className="text-[8px] font-bold"
                      style={{ color: "rgba(155, 176, 240, 0.85)" }}
                    >
                      O/U
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex size-6 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: "rgba(62, 97, 250, 0.15)" }}
                    >
                      <span
                        className="text-[10px] font-bold"
                        style={{ color: "rgba(155, 176, 240, 0.85)" }}
                      >
                        O/U
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <p className="font-semibold">Over/Under Game</p>
                      {burn.from && (
                        <p className="text-xs text-muted-foreground">
                          {burn.from.slice(0, 6)}...{burn.from.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : burn.source === BURN_SOURCE.BERATRACKR ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex size-4 items-center justify-center rounded-full"
                    style={{ background: "rgba(62, 97, 250, 0.15)" }}
                  >
                    <span
                      className="text-[8px] font-bold"
                      style={{ color: "rgba(155, 176, 240, 0.85)" }}
                    >
                      BT
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex size-6 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: "rgba(62, 97, 250, 0.15)" }}
                    >
                      <span
                        className="text-[10px] font-bold"
                        style={{ color: "rgba(155, 176, 240, 0.85)" }}
                      >
                        BT
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <p className="font-semibold">Beratrackr Activity</p>
                      {burn.from && (
                        <p className="text-xs text-muted-foreground">
                          {burn.from.slice(0, 6)}...{burn.from.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex size-4 items-center justify-center rounded-full"
                    style={{ background: "rgba(62, 97, 250, 0.1)" }}
                  >
                    <span
                      className="text-[8px] font-bold"
                      style={{ color: "rgba(155, 176, 240, 0.6)" }}
                    >
                      ?
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex size-6 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: "rgba(62, 97, 250, 0.1)" }}
                    >
                      <span
                        className="text-[10px] font-bold"
                        style={{ color: "rgba(155, 176, 240, 0.6)" }}
                      >
                        ?
                      </span>
                    </div>
                    <div className="flex flex-col">
                      <p className="font-semibold">Mystery Burn</p>
                      {burn.from && (
                        <p className="text-xs text-muted-foreground">
                          {burn.from.slice(0, 6)}...{burn.from.slice(-4)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </m.div>
        </TooltipProvider>

        {/* Amount and HENLO - Middle */}
        <m.div
          className="flex ml-6 flex-1 items-center gap-2"
          initial={isNewBurn ? { opacity: 0, y: 10 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: isNewBurn ? 0.65 : 0, // After icon appears
            ease: [0.4, 0, 0.2, 1],
          }}
        >
          <span
            className="font-pressStart2P text-sm transition-colors duration-200"
            style={{
              color: heatColors.text,
              textShadow: isNewest
                ? `0 0 6px ${heatColors.glow}`
                : index < 3
                  ? `0 0 3px ${heatColors.glow}`
                  : "none",
            }}
          >
            {amount}
          </span>
          <Image
            src="/token/henlo.webp"
            alt="HENLO"
            width={16}
            height={16}
            className="transition-[filter] duration-200"
            style={{
              filter: isNewest
                ? `drop-shadow(0 0 3px ${heatColors.glow})`
                : "none",
              opacity: hover ? 0.9 : 0.7,
            }}
          />
        </m.div>

        {/* Fire indicators - Last */}
        <m.div
          className="flex items-center gap-0.5"
          initial={isNewBurn ? { scale: 0, opacity: 0 } : false}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: "spring",
            bounce: 0,
            duration: 0.6,
            delay: isNewBurn ? 0.7 : 0, // After amount appears
          }}
        >
          {[...Array(fireCount)].map((_, i) => (
            <m.div
              key={i}
              className="relative size-3.5"
              initial={isNewBurn ? { scale: 0, rotate: 180 } : false}
              animate={{ scale: 1, rotate: 0 }}
              transition={{
                type: "spring",
                bounce: 0,
                duration: 0.4,
                delay: isNewBurn ? 0.75 + i * 0.05 : 0,
              }}
            >
              <Image
                src="/incinerator/bera_fire.png"
                alt=""
                fill
                sizes="14px"
                className="object-contain"
                style={{
                  filter: isNewest
                    ? `grayscale(0.55) brightness(0.9) drop-shadow(0 0 1px ${heatColors.glow})`
                    : `grayscale(0.7) brightness(${0.55 + heatIntensity * 0.2})`,
                }}
              />
            </m.div>
          ))}
        </m.div>
      </div>

      <m.span
        className="font-inter text-xs transition-opacity duration-200"
        initial={isNewBurn ? { opacity: 0, x: 20 } : false}
        animate={{ opacity: hover ? 0.7 : 1, x: 0 }}
        transition={{
          duration: 0.4,
          delay: isNewBurn ? 0.8 : 0, // Last element to appear
        }}
        style={{
          color: heatColors.timeText || heatColors.text,
        }}
      >
        {formatTimestamp(burn.timestamp)}
      </m.span>
    </Wrapper>
  );
});
